import {
  Fragment,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useStateWithCallbackInstant } from 'use-state-with-callback';
import { createSelector } from '@reduxjs/toolkit';
import { Grid, Tooltip, Typography } from '@mui/material';
import {
  EditOrderCarrierForm,
  EditOrderShipInfoForm,
  EditReshipWarehouseForm,
  ReshipReasonForm,
  LongTextHoverTooltip,
  ProcessOrderNotes,
  OrderDocumentUpload,
  LabelTrackingForm,
} from '..';
import {
  EditIcon,
  BusinessIcon,
  HomeIcon,
  DoneIcon,
  CloseIcon,
  WarningOutlinedIcon,
  PickNStickIcon,
  DescriptionIcon,
  NoteIcon,
} from '../../assets';
import PickAndStickSVG from '../../assets/images/pick-and-stick.svg';
import { orderStatusStyles } from '../../styles/sharedStyles';
import {
  formatCurrency,
  formatPhone,
  getReceiverName,
  formatDate,
  paths,
  generateUrl,
  pathParams,
  Navigation,
} from '../../lib';
import {
  useSelector,
  useDispatch,
  uiSelectors,
  systemSelectors,
  getProcessOrder,
  setSelectedOrder,
  updateReshipProperties,
  orderSelectors,
} from '../../state';
import { useOpenFile, useNotify } from '../../hooks';
import {
  ContactAPI,
  CustomsData,
  CarrierAdvancedOptions,
  DeliveryConfirmationType,
  OrderFulfillmentType,
  OrderCarrierAPI,
  OrderShipmentAPI,
  ProcessOrderAPI,
  ShippingSelectionPreferenceType,
  TruckingJobStatusType,
} from '../../types';
import { styles } from './DetailsTab.styles';

// get the reship state slice only if isReship
const selectReshipState = createSelector(
  (state) => state.orders.reshipState,
  (_, isReship) => isReship,
  (reshipState, isReship) => (isReship ? reshipState : null),
);

interface DetailsTabProps {
  children: React.ReactNode;
  header: string;
  selectedOrder: ProcessOrderAPI;
}

export function DetailsTab({
  children,
  header,
  selectedOrder,
}: DetailsTabProps) {
  const { classes, cx } = styles();
  const dispatch = useDispatch();
  const handleOpenFile = useOpenFile();
  const notify = useNotify();
  const statesObj = useSelector(systemSelectors.statesObj);

  const viewVersion = useSelector(uiSelectors.viewVersion);

  const isReship = useSelector(orderSelectors.isReship);
  const reshipState = useSelector((state) =>
    selectReshipState(state, isReship),
  );

  const [showEditCarrier, setShowEditCarrier] = useState(false);
  const [showEditShipInfo, setShowEditShipInfo] = useState(false);
  const [showEditWarehouse, setShowEditWarehouse] = useState(false);
  const [showEditReshipReason, setShowEditReshipReason] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [overrideCarrierName, setOverrideCarrierName] = useState('');
  const [overrideServiceName, setOverrideServiceName] = useState('');
  const [overrideFulfillmentDisplay, setOverrideFulfillmentDisplay] =
    useState('');
  const [overrideWarehouseName, setOverrideWarehouseName] = useState('');

  const [showDocEditModal, setShowDocEditModal] = useState(false);
  const [docEditId, setDocEditId] = useStateWithCallbackInstant<number | null>(
    null,
    (id) => setShowDocEditModal(!!id),
  );
  const [showLabelTracking, setShowLabelTracking] = useState(false);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    (async function () {
      const { data } = await dispatch(
        getProcessOrder({
          orderNumber:
            selectedOrder.orderCreationType === 'Extensiv'
              ? selectedOrder.externalID
              : selectedOrder.id!,
          shouldResetFields: false,
          isInternalOrderId: selectedOrder.orderCreationType !== 'Extensiv',
        }),
      );
      if (data) {
        const newOrderState = generateOrderState(selectedOrder, data);
        // check if changes to fulfillment type requires navigating to other packing flow
        const flowToNavigate = getFlowToNavigateTo(
          selectedOrder,
          newOrderState,
        );

        if (flowToNavigate) {
          const path =
            flowToNavigate === 'freightFlow'
              ? paths.FREIGHT_PACKING_MODE
              : paths.PACKING_MODE;

          Navigation.redirect(
            generateUrl(
              path,
              { [pathParams.ORDER_ID]: selectedOrder.id },
              {
                view: 'items',
                'should-reset': true,
                'is-internal-order-id': true,
              },
            ),
          );
        } else {
          dispatch(setSelectedOrder(newOrderState));
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewVersion]);

  const handleEditReshipShipInfo = useCallback(
    (resourceType?: 'carrier' | 'shipTo') => (data) => {
      const {
        id,
        carrierServiceName,
        carrierName,
        fulfillmentTypeDisplay,
        warehouseName,
        ...rest
      } = data;
      const payload = rest;
      resourceType === 'carrier' && (payload.hasOverrideCarrierInfo = true);
      resourceType === 'shipTo' && (payload.hasOverrideShipToInfo = true);
      dispatch(updateReshipProperties(payload));
      !!carrierName && setOverrideCarrierName(carrierName);
      !!carrierServiceName && setOverrideServiceName(carrierServiceName);
      !!fulfillmentTypeDisplay &&
        setOverrideFulfillmentDisplay(fulfillmentTypeDisplay);
      !!warehouseName && setOverrideWarehouseName(warehouseName);
      notify('Changes saved');
    },
    [dispatch, notify],
  );

  const {
    id: orderId,
    customerID,
    customerName,
    warehouseName,
    warehouseTimeZoneID,
    status,
    // statusDisplay,
    defaultPickAndStickMode,
    defaultShipsWholePallet,
    documents,
    notes,
    items,
    carrierInfo: {
      carrierDisplayName,
      carrierServiceName,
      fulfillmentTypeDisplay,
      invalidThirdPartyAccount,
      lastUpdatedBy,
      lastUpdatedOn,
    } = {},
    shipToContact: { person: { address: receiverAddress } = {} } = {},
  } = selectedOrder || {};

  const { shippingCategory } = receiverAddress || {};

  const {
    fulfillmentType,
    companyID,
    carrierRequestedID,
    carrierRequestedName,
    carrierServiceRequestedID,
    carrierServiceRequestedCode,
    carrierPackageCode,
    shippingSelectionPreference,
    thirdPartyAccountNumber,
    insuranceCost,
    requiresDeliveryConfirmation,
    confirmationType,
    customs,
    isInternationalShipment,
    advancedOptions,
    truckingJobPickupNote,
    truckingJobDestinationNote,
    billToContact,
  } = useMemo(
    () => getEditCarrierData(selectedOrder, isReship, reshipState) || {},
    [isReship, reshipState, selectedOrder],
  );

  const {
    shipToAddress1,
    shipToAddress2,
    shipToCity,
    shipToStateID,
    shipToStateAlternate,
    shipToZip,
    shipToCountryID,
    shipToCompanyName,
    shipToFirstName,
    shipToLastName,
    shipToPhone,
    shipToMobilePhone,
    shipToEmail,
    shipWithFailedAddressValidation,
    addressValidationMessages,
  } = useMemo(
    () => getEditShipData(selectedOrder, isReship, reshipState) || {},
    [isReship, reshipState, selectedOrder],
  );

  const editShipmentData = {
    shipToAddress1,
    shipToAddress2,
    shipToCity,
    shipToStateID,
    shipToStateAlternate,
    shipToZip,
    shipToCountryID,
    shipToCompanyName,
    shipToFirstName,
    shipToLastName,
    shipToPhone,
    shipToMobilePhone,
    shipToEmail,
    shipWithFailedAddressValidation,
    addressValidationMessages,
  };

  const prepareCarrierData = (): OrderCarrierAPI &
    OrderShipmentAPI & { items?: any } => {
    const data: OrderCarrierAPI & OrderShipmentAPI = {
      // OrderCarrierAPI properties
      fulfillmentType: (fulfillmentType as OrderFulfillmentType) || '',
      companyID,
      carrierRequestedID,
      carrierServiceRequestedID,
      carrierServiceCode: carrierServiceRequestedCode || '',
      carrierPackageCode: carrierPackageCode || '',
      shippingSelectionPreference:
        (shippingSelectionPreference as ShippingSelectionPreferenceType) || '',
      thirdPartyAccountNumber: thirdPartyAccountNumber || '',
      insuranceCost,
      customerID,
      requiresDeliveryConfirmation: requiresDeliveryConfirmation || false,
      confirmationType: (confirmationType as DeliveryConfirmationType) || '',
      customs,
      isInternationalShipment: isInternationalShipment || false,
      advancedOptions,
      truckingJobPickupNote,

      // OrderShipmentAPI properties
      fulfillmentTypeDisplay: fulfillmentType ? String(fulfillmentType) : '',
      carrierRequestedCompanyName: '',
      carrierRequestedNickname: carrierRequestedName || '',
      carrierServiceRequestedName: carrierServiceName || '',
      carrierUsedCompanyName: '',
      carrierUsedNickname: '',
      carrierServiceUsedName: '',
      invalidThirdPartyAccount: invalidThirdPartyAccount || false,
      shippedConfirmationTypeDisplay: '',
      receiverName: '',
      fullShippingAddress: '',
      shipToPhone: '',
      shipToMobilePhone: '',
      shipToEmail: '',
      shippingCategory: '',
      shipmentJobID: 0,
      shipmentJobStatus: null as unknown as TruckingJobStatusType,
      shipments: [],
      shipToAddressValidationStatus: '',
      addressValidationOverrideByFirstName: '',
      addressValidationOverrideByLastName: '',
      addressValidationMessages: addressValidationMessages || {},
      truckingJobDestinationNote,
      billToContact,
    };

    // Add items only if this is an international shipment
    if (isInternationalShipment && items) {
      return { ...data, items };
    }

    return data;
  };

  const editCarrierData = prepareCarrierData();

  const invalidAddressMessages = addressValidationMessages?.messages;

  return (
    <Fragment>
      <div className={classes.content}>
        <div className={classes.client}>{customerName}</div>
        <div className={classes.warehouse}>
          {overrideWarehouseName || warehouseName}
          <Fragment>
            {isReship && (
              <EditIcon
                className={classes.editIcon}
                style={{ marginLeft: 4 }}
                onClick={() => setShowEditWarehouse(true)}
              />
            )}
          </Fragment>
        </div>
        <div className={classes.row}>
          {/* <div className={classes.group}>
            <CheckCircleIcon className={classes.statusIcon} fontSize='small' />
            <div className={classes.status}>Ready to ship</div>
          </div> */}
          {
            isReship && !!reshipState?.reshipReturnReasonDisplay && (
              <div className={classes.sectionTitle}>
                <span
                  className={cx(classes.status)}
                  style={{
                    ...orderStatusStyles.Packed,
                    maxWidth: '100%',
                  }}
                >
                  {reshipState.reshipReturnReasonDisplay + ' '}
                </span>
                <EditIcon
                  className={classes.editIcon}
                  style={{ marginLeft: 4 }}
                  onClick={() => setShowEditReshipReason(true)}
                />
              </div>
            )
            //  : (
            //   <div
            //     className={cx(classes.status)}
            //     style={orderStatusStyles[status]}
            //   >
            //     {statusDisplay}
            //   </div>
            // )
          }
        </div>
        {defaultPickAndStickMode && (
          <div className={classes.row}>
            <div className={classes.flex}>
              <PickNStickIcon className={classes.pickNStickIcon} />
              <span>Pick and stick</span>
            </div>
          </div>
        )}
        {defaultShipsWholePallet && (
          <div className={classes.row}>
            <div className={classes.flex}>
              <PickAndStickSVG style={{ marginRight: 8 }} />
              <span>Pick and ship</span>
            </div>
          </div>
        )}
      </div>
      <div className={classes.divider}></div>
      <div className={classes.content}>
        <div className={classes.sectionTitle}>
          Ship to info{' '}
          <EditIcon
            className={classes.editIcon}
            style={{ marginLeft: 4 }}
            onClick={() => setShowEditShipInfo(true)}
          />
        </div>
        <div className={classes.shipName}>
          {getReceiverName({
            firstName: shipToFirstName,
            lastName: shipToLastName,
          })}
        </div>
        <div className={classes.shipName}>{shipToCompanyName}</div>
        {(!!shipToPhone || !!shipToMobilePhone || !!shipToEmail) && (
          <div
            className={classes.shipAddress}
            style={{ display: 'flex', gap: 24, marginBottom: 4 }}
          >
            {!!shipToPhone && <span>{formatPhone(shipToPhone)}</span>}
            {!!shipToMobilePhone && (
              <span>Mobile: {formatPhone(shipToMobilePhone)}</span>
            )}
            {!!shipToEmail && <span>{shipToEmail}</span>}
          </div>
        )}
        <div className={classes.flex} style={{ alignItems: 'flex-start' }}>
          {shippingCategory === 'Residential' && (
            <HomeIcon
              style={{ fontSize: 18, marginRight: 4 }}
              className={classes.shipAddress}
            />
          )}
          {shippingCategory === 'Commercial' && (
            <BusinessIcon
              style={{ fontSize: 18, marginRight: 4 }}
              className={classes.shipAddress}
            />
          )}
          <div className={classes.flexColumn}>
            <div className={classes.shipAddress}>{shipToAddress1}</div>
            <div className={classes.shipAddress}>{`${shipToCity || ''}, ${
              statesObj?.[shipToStateID]?.code || ''
            } ${shipToZip || ''}`}</div>
          </div>
        </div>
        {Array.isArray(invalidAddressMessages) && (
          <div
            className={classes.row}
            style={{ flexDirection: 'column', alignItems: 'flex-start' }}
          >
            {invalidAddressMessages.map((m, i) => {
              const { message } = m;
              return (
                <Typography
                  key={i}
                  component='span'
                  color='error'
                  style={{ fontSize: 14 }}
                >
                  {message}
                </Typography>
              );
            })}
          </div>
        )}
      </div>
      <div className={classes.divider}></div>
      <div className={classes.content}>
        <div className={classes.sectionTitle}>
          Carrier info{' '}
          <EditIcon
            className={classes.editIcon}
            style={{ marginLeft: 4 }}
            onClick={() => setShowEditCarrier(true)}
          />
        </div>
        <Grid container spacing={1} className={classes.grid}>
          <CarrierField
            title='Fulfillment type'
            value={overrideFulfillmentDisplay || fulfillmentTypeDisplay}
            fullWidth
          />
          <CarrierField
            title='Carrier'
            value={overrideCarrierName || carrierDisplayName}
          />
          <CarrierField
            title='Account nickname'
            value={
              <LongTextHoverTooltip
                text={carrierRequestedName || ''}
                maxLength={18}
              />
            }
          />
          <CarrierField
            title='Service'
            value={
              <LongTextHoverTooltip
                text={overrideServiceName || carrierServiceName || ''}
                maxLength={18}
              />
            }
          />
          <CarrierField
            title='Preferred method'
            value={shippingSelectionPreference}
          />
          {!!lastUpdatedBy && (
            <Grid
              className={classes.gridItem}
              size={{
                sm: 12,
              }}
            >
              <span className={classes.lastUpdated} style={{ marginTop: 0 }}>
                {`Last updated by ${lastUpdatedBy} ${
                  !!lastUpdatedOn
                    ? '- on ' +
                      formatDate(lastUpdatedOn, {
                        formatStr: 'Pp zzz',
                        useTime: true,
                        timeZone: warehouseTimeZoneID,
                      })
                    : ''
                }`}
              </span>
            </Grid>
          )}
        </Grid>
        <div
          className={classes.sectionTitle}
          style={{ fontWeight: 600, marginTop: 24 }}
        >
          Shipping options
        </div>
        <Grid container spacing={1} className={classes.grid}>
          <CarrierField title='Bill to' value={null} />
          <CarrierField
            title='Third party account'
            value={thirdPartyAccountNumber}
            errorMsg={
              invalidThirdPartyAccount
                ? 'Invalid Third party account'
                : undefined
            }
          />
          <CarrierField
            title='Confirmation'
            value={
              requiresDeliveryConfirmation ? (
                <DoneIcon style={{ fontSize: 18 }} />
              ) : (
                <CloseIcon style={{ fontSize: 18 }} />
              )
            }
          />
          <CarrierField
            title='Insurance amount'
            value={formatCurrency(insuranceCost)}
          />
        </Grid>
        {/* TODO implement when order completed */}
        {/* <Button color='primary' className={classes.viewRates}>
          view rates
        </Button> */}
      </div>
      <div className={classes.divider}></div>
      {orderId && (
        <>
          {showEditCarrier && (
            <EditOrderCarrierForm
              open={showEditCarrier}
              handleClose={() => setShowEditCarrier(false)}
              orderId={orderId}
              orderStatus={status}
              curData={editCarrierData}
              isProcessing
              customSubmit={
                isReship ? handleEditReshipShipInfo('carrier') : undefined
              }
            />
          )}
          {showEditShipInfo && (
            <EditOrderShipInfoForm
              open={showEditShipInfo}
              handleClose={() => setShowEditShipInfo(false)}
              orderId={orderId}
              curData={editShipmentData}
              customSubmit={
                isReship ? handleEditReshipShipInfo('shipTo') : undefined
              }
            />
          )}
        </>
      )}
      {showNotes && (
        <ProcessOrderNotes
          open={showNotes}
          handleClose={() => setShowNotes(false)}
          notes={notes}
        />
      )}
      {showEditWarehouse && (
        <EditReshipWarehouseForm
          open={showEditWarehouse}
          handleClose={() => setShowEditWarehouse(false)}
          warehouseID={reshipState?.warehouseID}
          callback={handleEditReshipShipInfo()}
        />
      )}
      {showEditReshipReason && (
        <ReshipReasonForm
          open={showEditReshipReason}
          handleClose={() => setShowEditReshipReason(false)}
          reshipReturnReason={reshipState?.reshipReturnReason}
          overrideShouldChargeToCustomer={
            reshipState?.overrideShouldChargeToCustomer
          }
          callback={handleEditReshipShipInfo()}
        />
      )}
      {!!notes?.length && (
        <Fragment>
          <div className={classes.content}>
            <div className={classes.sectionTitle}>
              Notes
              <NoteIcon
                className={classes.docIcon}
                onClick={() => setShowNotes(true)}
                style={{ fontSize: 18, marginLeft: 5 }}
              />
            </div>
          </div>
          <div className={classes.divider}></div>
        </Fragment>
      )}
      <div className={classes.content}>
        <div className={classes.sectionTitle}>Documents</div>
        {documents.map((d) => {
          const {
            id,
            documentFeeTypeDisplay,
            quantity,
            document: {
              id: docId,
              createdOn,
              name,
              objectName,
              description,
            } = {},
          } = d;

          return (
            <div className={classes.docSection} key={id}>
              <DescriptionIcon
                className={classes.docIcon}
                fontSize='small'
                onClick={() => handleOpenFile(objectName)}
              />
              <div>
                <div className={cx(classes.docName, classes.flex)}>
                  {name}
                  <EditIcon
                    className={classes.editIcon}
                    style={{ marginLeft: 4 }}
                    onClick={() =>
                      setDocEditId(typeof docId === 'number' ? docId : null)
                    }
                  />
                </div>
                <div className={classes.docDesc}>{`${
                  documentFeeTypeDisplay !== 'Other'
                    ? documentFeeTypeDisplay
                    : ''
                } created on ${formatDate(createdOn)}`}</div>
                {!!description && (
                  <div className={classes.docDesc}>
                    <LongTextHoverTooltip text={description} maxLength={45} />
                  </div>
                )}
                {!!quantity && (
                  <div
                    className={classes.docDesc}
                  >{`${quantity} page(s) billed to customer`}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {showDocEditModal && orderId && (
        <OrderDocumentUpload
          open={showDocEditModal}
          handleClose={() => setDocEditId(null)}
          orderId={orderId}
          documentId={docEditId}
          customerId={customerID}
          onSaveSuccess={(documentFeeType) => {
            if (
              documentFeeType === 'CartonLabelDocumentFee' &&
              fulfillmentType === 'LabelReceived'
            ) {
              setShowLabelTracking(true);
            }
          }}
        />
      )}
      {showLabelTracking && orderId && (
        <LabelTrackingForm
          open={showLabelTracking}
          handleClose={() => setShowLabelTracking(false)}
          orderId={orderId as number}
        />
      )}
    </Fragment>
  );
}

// function checkIfPickAndStick(lineItems) {
//   if (!lineItems) return false;
//   return lineItems.some((l) => l.hasEverPickedAndSticked);
// }

interface CarrierFieldProps {
  title: string;
  value: any;
  fullWidth?: boolean;
  errorMsg?: string;
}

function CarrierField({
  title,
  value,
  fullWidth,
  errorMsg,
}: CarrierFieldProps) {
  const { classes } = styles();
  return (
    <Grid
      size={{
        sm: fullWidth ? 12 : 6,
      }}
      className={classes.gridItem}
    >
      <div className={classes.carrierGroup}>
        <div className={classes.flex}>
          <div className={classes.carrierLabel}>{title}</div>
          {!!errorMsg && (
            <Tooltip title={errorMsg}>
              <WarningOutlinedIcon className={classes.errorIcon} />
            </Tooltip>
          )}
        </div>
        <div className={classes.carrierTxt}>{value ?? '--'}</div>
      </div>
    </Grid>
  );
}

function generateOrderState(
  curValues: ProcessOrderAPI,
  newValues: ProcessOrderAPI,
) {
  const {
    customerName,
    warehouseName,
    shipToCompanyName,
    carrierInfo,
    shipToContact,
    insuranceCost,
    documents,
  } = newValues;

  return {
    ...curValues,
    customerName,
    warehouseName,
    shipToCompanyName,
    carrierInfo,
    shipToContact,
    insuranceCost,
    documents,
  };
}

/**
 *  Check if when changing the fulfillment type we need to navigate to freight flow or vice versa
 * @param {object} curOrderState
 * @param {object} incomingOrderState
 * @returns {string} The flow to navigate to
 */
function getFlowToNavigateTo(
  curOrderState: ProcessOrderAPI,
  incomingOrderState: ProcessOrderAPI,
) {
  // Merged orders always use non-freight (small parcel) packing mode
  if (incomingOrderState.mergedOrderGroupProgress) {
    return null;
  }

  // check if we need to navigate to freight flow or vice versa
  let flowToNavigate: 'freightFlow' | 'smallParcelFlow' | null = null;
  const curFulfillmentType = curOrderState.carrierInfo?.fulfillmentType;
  const incomingFulfillmentType =
    incomingOrderState.carrierInfo?.fulfillmentType;

  if (curFulfillmentType || incomingFulfillmentType) {
    if (
      (!curFulfillmentType || curFulfillmentType === 'SmallParcel') &&
      (incomingFulfillmentType === 'LTL' || incomingFulfillmentType === 'FTL')
    ) {
      flowToNavigate = 'freightFlow';
    } else if (
      (curFulfillmentType === 'LTL' || curFulfillmentType === 'FTL') &&
      (!incomingFulfillmentType || incomingFulfillmentType === 'SmallParcel')
    ) {
      flowToNavigate = 'smallParcelFlow';
    }
  }

  return flowToNavigate;
}

function getEditShipData(selectedOrder, isReship, reshipState) {
  const {
    shipToCompanyName,
    shipToContact: {
      person: {
        firstName,
        lastName,
        address: receiverAddress,
        officePhone,
        mobilePhone,
        email,
      },
      shipWithFailedAddressValidation,
      addressValidationMessages,
    } = {},
  } = selectedOrder || {};
  const { address1, address2, city, stateID, countryID, stateAlternate, zip } =
    receiverAddress || {};

  const {
    hasOverrideShipToInfo,
    shipToCompanyName: reshipCompanyName,
    shipToFirstName,
    shipToLastName,
    shipToAddress1,
    shipToAddress2,
    shipToCity,
    shipToStateID,
    shipToStateAlternate,
    shipToZip,
    shipToCountryID,
    shipToPhone,
  } = reshipState || {};

  if (isReship && hasOverrideShipToInfo) {
    return {
      shipToAddress1,
      shipToAddress2,
      shipToCity,
      shipToStateID,
      shipToStateAlternate,
      shipToZip,
      shipToCountryID,
      shipToCompanyName: reshipCompanyName,
      shipToFirstName,
      shipToLastName,
      shipToPhone,
      shipToEmail: email,
    };
  } else {
    return {
      shipToAddress1: address1,
      shipToAddress2: address2,
      shipToCity: city,
      shipToStateID: stateID,
      shipToStateAlternate: stateAlternate,
      shipToZip: zip,
      shipToCountryID: countryID,
      shipToCompanyName,
      shipToFirstName: firstName,
      shipToLastName: lastName,
      shipToPhone: officePhone,
      shipToMobilePhone: mobilePhone,
      shipToEmail: email,
      shipWithFailedAddressValidation,
      addressValidationMessages,
    };
  }
}

interface EditCarrierDataReturn {
  // OrderCarrierAPI properties
  fulfillmentType?: OrderFulfillmentType | '';
  companyID?: number;
  carrierRequestedID?: number;
  carrierRequestedName?: string;
  carrierServiceRequestedID?: number;
  carrierServiceRequestedCode?: string;
  carrierPackageCode?: string;
  shippingSelectionPreference?: ShippingSelectionPreferenceType | '';
  thirdPartyAccountNumber?: string;
  insuranceCost?: number;
  customerID?: number | string;
  requiresDeliveryConfirmation?: boolean;
  confirmationType?: DeliveryConfirmationType | '';
  customs?: CustomsData;
  isInternationalShipment?: boolean;
  advancedOptions?: CarrierAdvancedOptions;
  truckingJobPickupNote?: string;
  truckingJobDestinationNote?: string;
  billToContact?: ContactAPI;

  // OrderShipmentAPI properties
  fulfillmentTypeDisplay?: string;
  carrierRequestedCompanyName?: string;
  carrierRequestedNickname?: string;
  carrierServiceRequestedName?: string;
  carrierUsedCompanyName?: string;
  carrierUsedNickname?: string;
  carrierServiceUsedName?: string;
  invalidThirdPartyAccount?: boolean;
  shippedConfirmationTypeDisplay?: string;
  receiverName?: string;
  fullShippingAddress?: string;
  shippingCategory?: string;
  shipmentJobID?: number;
  shipmentJobStatus?: string;
  shipments?: any[];
  shipToAddressValidationStatus?: string;
  addressValidationOverrideByFirstName?: string;
  addressValidationOverrideByLastName?: string;
  addressValidationMessages?: any;

  // Additional property for items
  items?: any[];
}

function getEditCarrierData(
  selectedOrder: ProcessOrderAPI,
  isReship: boolean,
  reshipState: any,
): EditCarrierDataReturn {
  const {
    customerID,
    items,
    carrierInfo: {
      carrierDisplayName,
      carrierServiceID,
      carrierServiceCode,
      carrierPackageCode,
      fulfillmentType,
      companyID,
      carrierID,
      carrierAccountID,
      shippingSelectionPreference,
      requiresDeliveryConfirmation,
      confirmationType,
      insuranceCost,
      customs,
      isInternationalShipment,
      advancedOptions,
      truckingJobPickupNote,
      truckingJobDestinationNote,
      billToContact,
    } = {},
  } = selectedOrder || {};

  const {
    hasOverrideCarrierInfo,
    fulfillmentType: reshipFulfillmentType,
    carrierID: reshipCarrierID,
    companyID: reshipCompanyID,
    carrierRequestedName,
    carrierServiceID: reshipCarrierServiceID,
    carrierServiceCode: reshipCarrierServiceCode,
    carrierPackageCode: reshipCarrierPackageCode,
    shippingSelectionPreference: reshipShippingSelectionPreference,
    thirdPartyAccountNumber: reshipThirdPartyAccountNumber,
    insuranceCost: reshipInsuranceCost,
    requiresDeliveryConfirmation: reshipRequiresDeliveryConfirmation,
    confirmationType: reshipConfirmationType,
  } = reshipState || {};

  // Common OrderShipmentAPI properties that need to be included in both cases
  const commonShipmentProps = {
    // Required properties from OrderShipmentAPI with default values
    fulfillmentTypeDisplay: '',
    carrierRequestedCompanyName: '',
    carrierRequestedNickname: '',
    carrierServiceRequestedName: '',
    carrierUsedCompanyName: '',
    carrierUsedNickname: '',
    carrierServiceUsedName: '',
    invalidThirdPartyAccount: false,
    shippedConfirmationTypeDisplay: '',
    receiverName: '',
    fullShippingAddress: '',
    shippingCategory: '',
    shipmentJobID: 0,
    shipmentJobStatus: '',
    shipments: [],
    shipToAddressValidationStatus: '',
    addressValidationOverrideByFirstName: '',
    addressValidationOverrideByLastName: '',
    addressValidationMessages: {},
  };

  if (isReship && hasOverrideCarrierInfo) {
    return {
      // OrderCarrierAPI properties
      fulfillmentType: reshipFulfillmentType || '',
      carrierRequestedID: reshipCarrierID,
      companyID: reshipCompanyID,
      carrierRequestedName: carrierRequestedName || '',
      carrierServiceRequestedID: reshipCarrierServiceID,
      carrierServiceRequestedCode: reshipCarrierServiceCode || '',
      carrierPackageCode: reshipCarrierPackageCode || '',
      shippingSelectionPreference: reshipShippingSelectionPreference || '',
      thirdPartyAccountNumber: reshipThirdPartyAccountNumber || '',
      insuranceCost: reshipInsuranceCost,
      customerID,
      requiresDeliveryConfirmation: reshipRequiresDeliveryConfirmation || false,
      confirmationType: reshipConfirmationType || '',
      // Include the common OrderShipmentAPI properties
      ...commonShipmentProps,
    };
  } else {
    return {
      // OrderCarrierAPI properties
      fulfillmentType: fulfillmentType || '',
      companyID,
      carrierRequestedID: carrierID,
      carrierRequestedName: carrierDisplayName || '',
      carrierServiceRequestedID: carrierServiceID,
      carrierServiceRequestedCode: carrierServiceCode || '',
      carrierPackageCode: carrierPackageCode || '',
      shippingSelectionPreference: shippingSelectionPreference || '',
      thirdPartyAccountNumber: carrierAccountID || '',
      insuranceCost,
      customerID,
      requiresDeliveryConfirmation: requiresDeliveryConfirmation || false,
      confirmationType: confirmationType || '',
      customs,
      isInternationalShipment,
      advancedOptions,
      truckingJobPickupNote,
      truckingJobDestinationNote,
      billToContact,
      // Only include items if this is an international shipment
      ...(isInternationalShipment ? { items } : {}),
      // Include the common OrderShipmentAPI properties
      ...commonShipmentProps,
    };
  }
}
